import React from 'react';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import css from './MoreCauses.module.css';
import pepalaLogo from './pepala-logo.png';
import fertilityFoundationLogo from './fertility-foundation-logo.png';
import dogAidScotlandLogo from './dog-aid-scotland-logo.jpg';
import londonKittiesLogo from './london-inner-city-kitties-logo.jpg';
import foodBankedLogo from './foodbanked-logo.png';
import ukBostonTerrierRescueLogo from './uk-boston-terrier-rescue-logo.png';

// A leading "The" is ignored when sorting, so "The Fertility Foundation"
// sorts as "Fertility Foundation".
const sortKey = name => name.replace(/^the\s+/i, '');

// Live charity partners. They are shown in A–Z order of `sortName` (sorted
// below, see `sortKey`), so a new partner can be added anywhere in this list.
const PARTNERS = [
  {
    key: 'fertilityFoundation',
    sortName: 'The Fertility Foundation',
    url: 'https://www.fertilityfoundation.org/',
    linkText: 'www.fertilityfoundation.org',
    logo: fertilityFoundationLogo,
    titleId: 'CustomLandingPage.moreCauses.fertilityTitle',
    textId: 'CustomLandingPage.moreCauses.fertilityText',
    linkAriaId: 'CustomLandingPage.moreCauses.fertilityLinkAria',
    logoAltId: 'CustomLandingPage.moreCauses.fertilityLogoAlt',
    categoryId: 'CustomLandingPage.moreCauses.fertilityCategory',
  },
  {
    key: 'pepala',
    sortName: 'Pepala',
    url: 'https://www.pepala.org.uk/',
    linkText: 'www.pepala.org.uk',
    logo: pepalaLogo,
    titleId: 'CustomLandingPage.moreCauses.featureTitle',
    textId: 'CustomLandingPage.moreCauses.featureText',
    linkAriaId: 'CustomLandingPage.moreCauses.pepalaLinkAria',
    logoAltId: 'CustomLandingPage.moreCauses.pepalaLogoAlt',
    categoryId: 'CustomLandingPage.moreCauses.pepalaCategory',
  },
  {
    key: 'dogAidScotland',
    sortName: 'Dog Aid Scotland',
    url: 'https://www.dogaidscotland.com/',
    linkText: 'www.dogaidscotland.com',
    logo: dogAidScotlandLogo,
    titleId: 'CustomLandingPage.moreCauses.dogAidTitle',
    textId: 'CustomLandingPage.moreCauses.dogAidText',
    linkAriaId: 'CustomLandingPage.moreCauses.dogAidLinkAria',
    logoAltId: 'CustomLandingPage.moreCauses.dogAidLogoAlt',
    categoryId: 'CustomLandingPage.moreCauses.dogAidCategory',
  },
  {
    key: 'londonKitties',
    sortName: 'London Inner City Kitties',
    url: 'https://www.london-inner-city-kitties.org/',
    linkText: 'www.london-inner-city-kitties.org',
    logo: londonKittiesLogo,
    titleId: 'CustomLandingPage.moreCauses.londonKittiesTitle',
    textId: 'CustomLandingPage.moreCauses.londonKittiesText',
    linkAriaId: 'CustomLandingPage.moreCauses.londonKittiesLinkAria',
    logoAltId: 'CustomLandingPage.moreCauses.londonKittiesLogoAlt',
    categoryId: 'CustomLandingPage.moreCauses.londonKittiesCategory',
  },
  {
    key: 'foodBanked',
    sortName: 'FoodBanked',
    url: 'https://www.foodbanked.org.uk/',
    linkText: 'www.foodbanked.org.uk',
    logo: foodBankedLogo,
    titleId: 'CustomLandingPage.moreCauses.foodBankedTitle',
    textId: 'CustomLandingPage.moreCauses.foodBankedText',
    linkAriaId: 'CustomLandingPage.moreCauses.foodBankedLinkAria',
    logoAltId: 'CustomLandingPage.moreCauses.foodBankedLogoAlt',
    categoryId: 'CustomLandingPage.moreCauses.foodBankedCategory',
  },
  {
    key: 'ukBostonTerrierRescue',
    sortName: 'UK Boston Terrier Rescue',
    url: 'https://www.ukbostonterrierrescue.co.uk/',
    linkText: 'www.ukbostonterrierrescue.co.uk',
    logo: ukBostonTerrierRescueLogo,
    titleId: 'CustomLandingPage.moreCauses.ukBostonTerrierTitle',
    textId: 'CustomLandingPage.moreCauses.ukBostonTerrierText',
    linkAriaId: 'CustomLandingPage.moreCauses.ukBostonTerrierLinkAria',
    logoAltId: 'CustomLandingPage.moreCauses.ukBostonTerrierLogoAlt',
    categoryId: 'CustomLandingPage.moreCauses.ukBostonTerrierCategory',
  },
].sort((a, b) => sortKey(a.sortName).localeCompare(sortKey(b.sortName), 'en'));

const MoreCauses = () => {
  const intl = useIntl();

  return (
    <section className={css.section}>
      <div className={css.inner}>
        <span className={css.eyebrow}>
          <FormattedMessage id="CustomLandingPage.moreCauses.eyebrow" />
        </span>

        <h2 className={css.heading}>
          <FormattedMessage
            id="CustomLandingPage.moreCauses.heading"
            values={{
              accent: (
                <span className={css.headingAccent}>
                  <FormattedMessage id="CustomLandingPage.moreCauses.headingAccent" />
                </span>
              ),
            }}
          />
        </h2>

        <p className={css.intro}>
          <FormattedMessage id="CustomLandingPage.moreCauses.intro" />
        </p>

        {/* Founding charity partner cards — one per partner, side by side. */}
        <div className={css.partnerGrid}>
          {PARTNERS.map(partner => (
            <div className={css.featureCard} key={partner.key}>
              <div className={css.logoBox}>
                <a
                  className={css.logoLink}
                  href={partner.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={intl.formatMessage({ id: partner.linkAriaId })}
                >
                  <img
                    className={css.logo}
                    src={partner.logo}
                    alt={intl.formatMessage({ id: partner.logoAltId })}
                  />
                </a>
              </div>

              <div className={css.featureContent}>
                <h3 className={css.featureTitle}>
                  <FormattedMessage id={partner.titleId} />
                </h3>
                <p className={css.featureText}>
                  <FormattedMessage id={partner.textId} />
                </p>
              </div>

              {/* Website link on the left, cause type on the right. */}
              <div className={css.featureFooter}>
                <a
                  className={css.featureLink}
                  href={partner.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {partner.linkText}
                </a>
                <span className={css.featureCategory}>
                  <FormattedMessage id={partner.categoryId} />
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* "Your Marketplace. Your Causes." panel — title on the left, the
            plan for future causes and the contact line on the right. */}
        <div className={css.panel}>
          <h3 className={css.panelTitle}>
            <FormattedMessage
              id="CustomLandingPage.moreCauses.panelTitle"
              values={{
                accent: (
                  <span className={css.panelTitleAccent}>
                    <FormattedMessage id="CustomLandingPage.moreCauses.panelTitleAccent" />
                  </span>
                ),
              }}
            />
          </h3>
          <div className={css.panelBody}>
            <p className={css.panelText}>
              <FormattedMessage id="CustomLandingPage.moreCauses.panelText" />
            </p>
            <p className={css.panelContact}>
              <FormattedMessage
                id="CustomLandingPage.moreCauses.panelContact"
                values={{
                  email: (
                    <a className={css.emailLink} href="mailto:info@causee.com">
                      info@causee.com
                    </a>
                  ),
                }}
              />
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MoreCauses;
